import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import CandidateRow from './components/CandidateRow';
import InterviewEvaluationModal from './components/InterviewEvaluationModal';
import ScheduleInterviewModal from './components/ScheduleInterviewModal';
import { apiFetch, getUserFacingErrorMessage, throwIfNotOk } from './services/apiClient';
import {
    getQuestionnaireForInterviewModal,
    normalizeInterviewEvaluationResults,
    getCandidateFitScore,
} from './utils/interviewEvalUtils';
import { resolveCandidateContact } from './utils/candidateAnalysisUtils';
import { getCachedUser } from './services/authService';
import './JobsPage.css';
import './CandidateListPage.css';

const CandidateListPage = () => {
    const { jobId } = useParams();
    const [jobDetails, setJobDetails] = useState(null);
    const [activeCandidateId, setActiveCandidateId] = useState(null);
    const [activeCandidateTab, setActiveCandidateTab] = useState('summary');
    const [error, setError] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [isInterviewEvaluationModalOpen, setIsInterviewEvaluationModalOpen] = useState(false);
    const [selectedResumeForEvaluation, setSelectedResumeForEvaluation] = useState(null);
    const [isScheduleModalOpen, setIsScheduleModalOpen] = useState(false);
    const [selectedResumeForSchedule, setSelectedResumeForSchedule] = useState(null);
    const jobDeletedRef = useRef(false);
    
    // Filter and sort state
    const [sortBy, setSortBy] = useState('best-fit'); // best-fit, score-high, score-low, name
    const [showFilter, setShowFilter] = useState('all'); // all, eligible, blocked, review-needed
    const [searchQuery, setSearchQuery] = useState('');
    const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
    const [minConfidence, setMinConfidence] = useState('');
    const [minBenchmark, setMinBenchmark] = useState('');
    
    // Candidate selection (for bulk rejection / recruiter screening)
    const [selectedCandidates, setSelectedCandidates] = useState(new Set());
    const [showRejectModal, setShowRejectModal] = useState(false);
    const [rejectSubject, setRejectSubject] = useState('Update on your application');
    const [rejectCustomMessage, setRejectCustomMessage] = useState('');
    const [rejectCompanyName, setRejectCompanyName] = useState('');
    const [rejectSending, setRejectSending] = useState(false);
    const [rejectResult, setRejectResult] = useState(null);

    const [showScreeningInviteModal, setShowScreeningInviteModal] = useState(false);
    const [screeningRecruiterEmail, setScreeningRecruiterEmail] = useState('');
    const [screeningDuration, setScreeningDuration] = useState(30);
    const [screeningSending, setScreeningSending] = useState(false);
    const [screeningResult, setScreeningResult] = useState(null);

    // Keyboard navigation
    const [focusedIndex, setFocusedIndex] = useState(-1);
    const candidateListRef = useRef(null);

    const fetchJobDetails = useCallback(async () => {
        try {
            const response = await apiFetch(`/api/jobs/${jobId}`);

            if (response.status === 404) {
                jobDeletedRef.current = true;
                setJobDetails(null);
                setError('Job not found. It may have been deleted.');
                return null;
            }

            await throwIfNotOk(response);

            const data = await response.json();
            setJobDetails(data);
            return data;
        } catch (err) {
            setError(err.message);
            return null;
        } finally {
            setIsLoading(false);
        }
    }, [jobId]);

    useEffect(() => {
        jobDeletedRef.current = false;
        fetchJobDetails();

        // Auto-refresh every 5 seconds if processing
        const interval = setInterval(() => {
            if (jobDeletedRef.current) return;
            fetchJobDetails();
        }, 5000);

        return () => clearInterval(interval);
    }, [jobId, fetchJobDetails]);

    // Extract job title from description
    const extractJobTitle = (description) => {
        if (!description) return 'Untitled Job';
        
        const filePrefixMatch = description.match(/^File:\s*([^\n]+)\n/);
        if (filePrefixMatch) {
            const filename = filePrefixMatch[1];
            return filename.replace(/\.[^.]*$/, '');
        }
        
        const lines = description.split('\n').filter(line => line.trim().length > 0);
        if (lines.length > 0) {
            const firstLine = lines[0].trim();
            const cleaned = firstLine
                .replace(/^Job description from file:\s*/i, '')
                .replace(/^Job description from files:\s*/i, '')
                .trim();
            
            if (cleaned.length > 100) {
                return cleaned.substring(0, 100).trim() + '...';
            }
            
            return cleaned || 'Untitled Job';
        }
        
        return description.substring(0, 100) || 'Untitled Job';
    };

    // Get candidate score helper
    const getCandidateScore = (candidate) => {
        if (candidate.rag_analysis?.evaluation_result?.calibrated_score !== undefined) {
            return candidate.rag_analysis.evaluation_result.calibrated_score;
        } else if (candidate.rag_analysis?.evaluation_result?.fit_score !== undefined) {
            return candidate.rag_analysis.evaluation_result.fit_score;
        } else if (candidate.analysis?.fit_score !== undefined) {
            return candidate.analysis.fit_score;
        } else if (candidate.analysis?.score !== undefined) {
            return candidate.analysis.score;
        }
        return 0;
    };

    // Get candidate eligibility helper
    const getCandidateEligibility = (candidate) => {
        if (candidate.rag_analysis?.evaluation_result?.eligibility_status) {
            return candidate.rag_analysis.evaluation_result.eligibility_status;
        } else if (candidate.analysis?.bucket) {
            const bucket = candidate.analysis.bucket.toLowerCase();
            if (bucket.includes('top-tier') || bucket.includes('strong-fit') || bucket.includes('good-fit')) {
                return 'eligible';
            } else if (bucket.includes('not-a-fit') || bucket.includes('lacks-experience')) {
                return 'ineligible';
            }
        }
        return 'review-needed';
    };

    // Get candidate name helper
    const getCandidateName = (candidate) => {
        return candidate.candidate_name || candidate.filename || 'Unknown Candidate';
    };

    // Filter and sort candidates
    const filteredAndSortedCandidates = useMemo(() => {
        if (!jobDetails?.resumes) return [];
        
        let candidates = [...jobDetails.resumes];
        
        // Apply search filter
        if (searchQuery.trim()) {
            const query = searchQuery.toLowerCase();
            candidates = candidates.filter(candidate => {
                const name = getCandidateName(candidate).toLowerCase();
                return name.includes(query);
            });
        }
        
        // Apply show filter
        if (showFilter !== 'all') {
            candidates = candidates.filter(candidate => {
                const eligibility = getCandidateEligibility(candidate);
                if (showFilter === 'eligible') return eligibility === 'eligible';
                if (showFilter === 'blocked') return eligibility === 'ineligible';
                if (showFilter === 'review-needed') return eligibility === 'review-needed';
                return true;
            });
        }
        
        // Apply advanced filters
        if (showAdvancedFilters) {
            if (minConfidence) {
                candidates = candidates.filter(candidate => {
                    const confidence = candidate.rag_analysis?.evaluation_result?.evidence_confidence;
                    return confidence !== undefined && confidence * 100 >= parseFloat(minConfidence);
                });
            }
            if (minBenchmark) {
                candidates = candidates.filter(candidate => {
                    const benchmark = candidate.rag_analysis?.evaluation_result?.calibration_metadata?.benchmark_percentile;
                    return benchmark !== undefined && benchmark * 100 >= parseFloat(minBenchmark);
                });
            }
        }
        
        // Apply sorting
        candidates.sort((a, b) => {
            if (sortBy === 'name') {
                return getCandidateName(a).localeCompare(getCandidateName(b));
            }
            
            const scoreA = getCandidateScore(a);
            const scoreB = getCandidateScore(b);
            
            if (sortBy === 'score-high') {
                return scoreB - scoreA;
            } else if (sortBy === 'score-low') {
                return scoreA - scoreB;
            } else {
                // best-fit: sort by score descending (default)
                return scoreB - scoreA;
            }
        });
        
        return candidates;
    }, [jobDetails, sortBy, showFilter, searchQuery, showAdvancedFilters, minConfidence, minBenchmark]);

    const handleCandidateClick = useCallback((candidateId) => {
        // #region agent log
        console.log('[DEBUG] handleCandidateClick called', { candidateId, currentActiveId: activeCandidateId, currentTab: activeCandidateTab });
        // #endregion
        if (activeCandidateId === candidateId) {
            // Collapse if clicking the same candidate
            setActiveCandidateId(null);
            setActiveCandidateTab('summary'); // Reset tab
        } else {
            // Expand new candidate (automatically collapses others)
            setActiveCandidateId(candidateId);
            setActiveCandidateTab('summary'); // Reset to summary tab
        }
    }, [activeCandidateId, activeCandidateTab]);
    
    const handleRowClick = useCallback((candidateId) => {
        handleCandidateClick(candidateId);
        // Update focused index
        const index = filteredAndSortedCandidates.findIndex(c => c.id === candidateId);
        if (index >= 0) {
            setFocusedIndex(index);
        }
    }, [filteredAndSortedCandidates, handleCandidateClick]);
    
    // Keyboard navigation handler
    useEffect(() => {
        const handleKeyDown = (e) => {
            if (!candidateListRef.current) return;
            
            if (e.key === 'ArrowDown') {
                e.preventDefault();
                setFocusedIndex(prev => {
                    const next = Math.min(prev + 1, filteredAndSortedCandidates.length - 1);
                    return next;
                });
            } else if (e.key === 'ArrowUp') {
                e.preventDefault();
                setFocusedIndex(prev => Math.max(prev - 1, 0));
            } else if (e.key === 'Enter' && focusedIndex >= 0) {
                e.preventDefault();
                const candidate = filteredAndSortedCandidates[focusedIndex];
                if (candidate) {
                    handleRowClick(candidate.id);
                }
            }
        };
        
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [focusedIndex, filteredAndSortedCandidates, handleRowClick]);
    
    const handleTabChange = (tabId) => {
        // #region agent log
        console.log('[DEBUG] handleTabChange called', { newTabId: tabId, currentActiveTab: activeCandidateTab, isHumanInLoop: tabId === 'human-in-the-loop', stackTrace: new Error().stack });
        fetch('http://127.0.0.1:7242/ingest/f0c51a36-2f20-40c5-b0fa-37899de60541',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'CandidateListPage.js:handleTabChange',message:'Tab change handler called',data:{newTabId:tabId,currentActiveTab:activeCandidateTab,isHumanInLoop:tabId==='human-in-the-loop'},timestamp:Date.now(),sessionId:'debug-session',runId:'tab-debug',hypothesisId:'B'})}).catch(()=>{});
        // #endregion
        console.log('[DEBUG] About to call setActiveCandidateTab with', tabId);
        setActiveCandidateTab(tabId);
        console.log('[DEBUG] setActiveCandidateTab called with', tabId);
        
        // Check if state actually updated after a brief delay
        setTimeout(() => {
            console.log('[DEBUG] State check after setActiveCandidateTab', { expectedTab: tabId });
        }, 100);
    };

    const handleCloseScheduleModal = () => {
        setIsScheduleModalOpen(false);
        setSelectedResumeForSchedule(null);
    };

    const handleScheduleSubmit = useCallback(async (interviewData) => {
        try {
            const response = await apiFetch('/api/google-schedule-interview', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(interviewData)
            });
            await throwIfNotOk(response);
            await response.json();
            alert('Available time slots sent to candidate! The candidate will receive an email with available time slots to choose from.');
            handleCloseScheduleModal();
            fetchJobDetails();
        } catch (error) {
            console.error('Error scheduling interview:', error);
            alert(`Error scheduling interview: ${error.message}`);
            throw error;
        }
    }, []);

    const toggleCandidateSelect = useCallback((resumeId) => {
        setSelectedCandidates(prev => {
            const next = new Set(prev);
            if (next.has(resumeId)) {
                next.delete(resumeId);
            } else {
                next.add(resumeId);
            }
            return next;
        });
    }, []);

    const toggleSelectAll = useCallback(() => {
        setSelectedCandidates(prev => {
            if (prev.size === filteredAndSortedCandidates.length && prev.size > 0) {
                return new Set();
            }
            return new Set(filteredAndSortedCandidates.map(c => c.id));
        });
    }, [filteredAndSortedCandidates]);

    const handleSendRejection = useCallback(async () => {
        if (selectedCandidates.size === 0) return;
        setRejectSending(true);
        setRejectResult(null);
        try {
            const response = await apiFetch(`/api/jobs/${jobId}/reject-candidates`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    resume_ids: Array.from(selectedCandidates),
                    subject: rejectSubject,
                    custom_message: rejectCustomMessage || null,
                    company_name: rejectCompanyName || null,
                }),
            });
            if (!response.ok) {
                const err = await response.json().catch(() => ({}));
                throw new Error(
                    getUserFacingErrorMessage(response.status, err, err.detail || `Server error ${response.status}`)
                );
            }
            const result = await response.json();
            setRejectResult(result);
        } catch (error) {
            setRejectResult({ error: error.message });
        } finally {
            setRejectSending(false);
        }
    }, [jobId, selectedCandidates, rejectSubject, rejectCustomMessage, rejectCompanyName]);

    const closeRejectModal = useCallback(() => {
        setShowRejectModal(false);
        setRejectResult(null);
        setRejectCustomMessage('');
        if (rejectResult && rejectResult.sent > 0) {
            setSelectedCandidates(new Set());
        }
    }, [rejectResult]);

    const openRejectModal = useCallback(() => {
        setRejectResult(null);
        const companyName = jobDetails?.company_name || getCachedUser()?.company?.name || '';
        setRejectCompanyName(companyName);
        setShowRejectModal(true);
    }, [jobDetails]);

    const openScreeningInviteModal = useCallback(() => {
        setScreeningResult(null);
        const email =
            getCachedUser()?.email ||
            jobDetails?.hiring_manager_email ||
            '';
        setScreeningRecruiterEmail(email);
        setScreeningDuration(30);
        setShowScreeningInviteModal(true);
    }, [jobDetails]);

    const closeScreeningInviteModal = useCallback(() => {
        setShowScreeningInviteModal(false);
        setScreeningResult(null);
        if (screeningResult && screeningResult.sent > 0) {
            setSelectedCandidates(new Set());
            fetchJobDetails();
        }
    }, [screeningResult, fetchJobDetails]);

    const handleSendScreeningInvites = useCallback(async () => {
        if (!jobId || selectedCandidates.size === 0) return;
        const email = (screeningRecruiterEmail || '').trim();
        if (!email || !email.includes('@')) {
            setScreeningResult({ error: 'Enter a valid recruiter email for Teams calendar.' });
            return;
        }
        setScreeningSending(true);
        setScreeningResult(null);
        try {
            const res = await apiFetch(`/api/recruiter-screening/jobs/${jobId}/bulk-invite`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    resume_ids: Array.from(selectedCandidates),
                    recruiter_email: email,
                    duration_minutes: Number(screeningDuration) || 30,
                }),
            });
            const data = await res.json().catch(() => ({}));
            if (!res.ok) {
                throw new Error(
                    getUserFacingErrorMessage(
                        res.status,
                        data,
                        data.detail || data.error || `Server error ${res.status}`
                    )
                );
            }
            setScreeningResult(data);
        } catch (err) {
            setScreeningResult({ error: err.message || 'Failed to send screening invites' });
        } finally {
            setScreeningSending(false);
        }
    }, [jobId, selectedCandidates, screeningRecruiterEmail, screeningDuration]);

    if (isLoading) {
        return (
            <div className="candidate-decision-board">
                <div className="loading-message">Loading candidates...</div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="candidate-decision-board">
                <div className="error-message">
                    Error: {error}{' '}
                    <div style={{ marginTop: '0.75rem' }}>
                        <Link to="/jobs" className="back-link">← Back to Jobs</Link>
                    </div>
                </div>
            </div>
        );
    }

    if (!jobDetails) {
        return (
            <div className="candidate-decision-board">
                <div className="error-message">Job not found</div>
            </div>
        );
    }

    const jobTitle = extractJobTitle(jobDetails.description);

    return (
        <div className="candidate-decision-board">
            {/* Clean Executive Header */}
            <div className="decision-board-header">
                <Link to="/jobs" className="back-link">← Back to Jobs</Link>
                <h1 className="decision-board-title">{jobTitle}</h1>
                <div className="decision-board-meta">
                    <span className="candidate-count-text">
                        {filteredAndSortedCandidates.length} {filteredAndSortedCandidates.length === 1 ? 'candidate evaluated' : 'candidates evaluated'}
                    </span>
                    <span className="scoring-context-text">
                        Scoring dynamically calibrated from this JD
                    </span>
                </div>
            </div>

            {/* Filter + Sort Bar */}
            <div className="decision-controls-bar">
                <div className="controls-left">
                    <div className="control-group">
                        <label htmlFor="sort-select">Sort by:</label>
                        <select 
                            id="sort-select"
                            className="control-select"
                            value={sortBy}
                            onChange={(e) => setSortBy(e.target.value)}
                        >
                            <option value="best-fit">Best Fit</option>
                            <option value="score-high">Score (High to Low)</option>
                            <option value="score-low">Score (Low to High)</option>
                            <option value="name">Name (A-Z)</option>
                        </select>
                    </div>
                    <div className="control-group">
                        <label htmlFor="show-select">Show:</label>
                        <select 
                            id="show-select"
                            className="control-select"
                            value={showFilter}
                            onChange={(e) => setShowFilter(e.target.value)}
                        >
                            <option value="all">All</option>
                            <option value="eligible">Eligible Only</option>
                            <option value="blocked">Blocked Only</option>
                            <option value="review-needed">Review Needed</option>
                        </select>
                    </div>
                    <div className="control-group search-group">
                        <label htmlFor="search-input">Search:</label>
                        <input
                            id="search-input"
                            type="text"
                            className="control-search"
                            placeholder="Candidate name…"
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                        />
                    </div>
                </div>
                <div className="controls-right">
                    <label className="select-all-label">
                        <input
                            type="checkbox"
                            checked={filteredAndSortedCandidates.length > 0 && selectedCandidates.size === filteredAndSortedCandidates.length}
                            onChange={toggleSelectAll}
                        />
                        {selectedCandidates.size > 0 && selectedCandidates.size === filteredAndSortedCandidates.length
                            ? 'Deselect All'
                            : 'Select All'}
                    </label>
                    {selectedCandidates.size > 0 && (
                        <>
                            <button
                                className="btn-send-rejection-inline"
                                onClick={openScreeningInviteModal}
                                type="button"
                            >
                                Send Recruiter Screening ({selectedCandidates.size})
                            </button>
                            <button
                                className="btn-send-rejection-inline"
                                onClick={openRejectModal}
                                type="button"
                            >
                                Send Rejection Email ({selectedCandidates.size})
                            </button>
                        </>
                    )}
                    <button 
                        className="advanced-filters-toggle"
                        onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
                    >
                        ⚙ Advanced Filters
                    </button>
                </div>
            </div>

            {/* Advanced Filters (Collapsible) */}
            {showAdvancedFilters && (
                <div className="advanced-filters-panel">
                    <div className="advanced-filter-group">
                        <label htmlFor="min-confidence">Confidence ≥</label>
                        <input
                            id="min-confidence"
                            type="number"
                            className="advanced-filter-input"
                            placeholder="0"
                            min="0"
                            max="100"
                            value={minConfidence}
                            onChange={(e) => setMinConfidence(e.target.value)}
                        />
                        <span className="filter-unit">%</span>
                    </div>
                    <div className="advanced-filter-group">
                        <label htmlFor="min-benchmark">Benchmark Percentile ≥</label>
                        <input
                            id="min-benchmark"
                            type="number"
                            className="advanced-filter-input"
                            placeholder="0"
                            min="0"
                            max="100"
                            value={minBenchmark}
                            onChange={(e) => setMinBenchmark(e.target.value)}
                        />
                        <span className="filter-unit">%</span>
                    </div>
                </div>
            )}

            {/* Main Content Area */}
            <div className="decision-board-content">
                {/* Candidate List (Left) */}
                <div className="candidate-list-section" ref={candidateListRef}>
                    {filteredAndSortedCandidates.length === 0 ? (
                        <div className="no-candidates-message">
                            <p>No candidates found matching your filters.</p>
                        </div>
                    ) : (
                        filteredAndSortedCandidates.map((candidate, index) => (
                            <CandidateRow
                                key={candidate.id}
                                candidate={candidate}
                                jobTitle={jobTitle}
                                jobCompanyName={jobDetails?.company_name || null}
                                jobDescription={jobDetails?.description}
                                rank={index + 1}
                                isExpanded={activeCandidateId === candidate.id}
                                isFocused={focusedIndex === index}
                                activeTab={activeCandidateTab}
                                isSelected={selectedCandidates.has(candidate.id)}
                                onToggleSelect={toggleCandidateSelect}
                                onExpand={() => handleRowClick(candidate.id)}
                                onTabChange={handleTabChange}
                                onQuestionnaireSaved={async (candidateId) => {
                                    const data = await fetchJobDetails();
                                    if (data?.resumes) {
                                        setJobDetails(data);
                                        const resume = data.resumes.find(r => r.id === candidateId);
                                        if (resume) {
                                            setSelectedResumeForEvaluation(resume);
                                            setIsInterviewEvaluationModalOpen(true);
                                        }
                                    }
                                }}
                                onEvaluateInterview={(candidate) => {
                                    setSelectedResumeForEvaluation(candidate);
                                    setIsInterviewEvaluationModalOpen(true);
                                }}
                                onScheduleInterview={(candidate) => {
                                    setSelectedResumeForSchedule(candidate);
                                    setIsScheduleModalOpen(true);
                                }}
                                onEvaluationSaved={async () => {
                                    const data = await fetchJobDetails();
                                    if (data?.resumes) {
                                        setSelectedResumeForEvaluation((prev) => {
                                            if (!prev?.id) return prev;
                                            const fresh = data.resumes.find(
                                                (r) => Number(r.id) === Number(prev.id)
                                            );
                                            return fresh || prev;
                                        });
                                    }
                                }}
                                onPhoneScreeningSaved={async () => {
                                    await fetchJobDetails();
                                }}
                            />
                        ))
                    )}
                </div>
            </div>

            {isInterviewEvaluationModalOpen && selectedResumeForEvaluation && (
                <InterviewEvaluationModal
                    isOpen={isInterviewEvaluationModalOpen}
                    onClose={() => {
                        setIsInterviewEvaluationModalOpen(false);
                        setSelectedResumeForEvaluation(null);
                    }}
                    questionnaire={getQuestionnaireForInterviewModal(selectedResumeForEvaluation)}
                    evaluationCompleted={selectedResumeForEvaluation?.evaluation_completed}
                    evaluationResults={selectedResumeForEvaluation?.evaluation_results}
                    evaluationPayload={selectedResumeForEvaluation?.evaluation_payload}
                    candidateName={resolveCandidateContact(selectedResumeForEvaluation).name}
                    fitScore={getCandidateFitScore(selectedResumeForEvaluation)}
                    jobTitle={jobTitle || selectedResumeForEvaluation?.analysis?.job_title || 'Position'}
                    resumeId={selectedResumeForEvaluation?.id}
                    jobId={jobId ? parseInt(jobId, 10) : null}
                    candidateEmail={resolveCandidateContact(selectedResumeForEvaluation).email}
                    jobCompanyName={jobDetails?.company_name || null}
                    onEvaluationSaved={async () => {
                        const data = await fetchJobDetails();
                        if (data?.resumes && selectedResumeForEvaluation?.id) {
                            const updatedResume = data.resumes.find(
                                (r) => r.id === selectedResumeForEvaluation.id
                            );
                            if (updatedResume) {
                                setSelectedResumeForEvaluation(updatedResume);
                            }
                        }
                    }}
                />
            )}

            {isScheduleModalOpen && selectedResumeForSchedule && (
                <ScheduleInterviewModal
                    isOpen={isScheduleModalOpen}
                    onClose={handleCloseScheduleModal}
                    candidate={selectedResumeForSchedule}
                    jobId={parseInt(jobId, 10)}
                    onSchedule={handleScheduleSubmit}
                />
            )}

            {/* Rejection Email Modal */}
            {showRejectModal && (
                <div className="reject-modal-overlay" onClick={(e) => { if (e.target === e.currentTarget) closeRejectModal(); }}>
                    <div className="reject-modal">
                        <div className="reject-modal-header">
                            <h2>Send Rejection Email</h2>
                            <button className="reject-modal-close" onClick={closeRejectModal}>&times;</button>
                        </div>

                        {!rejectResult ? (
                            <div className="reject-modal-body">
                                {/* Pre-send candidate preview */}
                                <div className="reject-candidate-preview">
                                    <table className="reject-preview-table">
                                        <thead>
                                            <tr>
                                                <th>Candidate</th>
                                                <th>Email</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {filteredAndSortedCandidates
                                                .filter(c => selectedCandidates.has(c.id))
                                                .map(c => (
                                                    <tr key={c.id} className={!c.email ? 'no-email-row' : ''}>
                                                        <td>{c.candidate_name || c.filename || 'Unknown'}</td>
                                                        <td>{c.email || <span className="no-email-label">No email on file</span>}</td>
                                                    </tr>
                                                ))
                                            }
                                        </tbody>
                                    </table>
                                    {filteredAndSortedCandidates.filter(c => selectedCandidates.has(c.id) && !c.email).length > 0 && (
                                        <p className="reject-warning">
                                            {filteredAndSortedCandidates.filter(c => selectedCandidates.has(c.id) && !c.email).length} candidate(s) have no email on file and will be skipped.
                                        </p>
                                    )}
                                </div>

                                <div className="reject-form-group">
                                    <label>Company Name</label>
                                    <input
                                        type="text"
                                        value={rejectCompanyName}
                                        onChange={(e) => setRejectCompanyName(e.target.value)}
                                        className="reject-input"
                                        placeholder="Your company name (shown in the email)"
                                    />
                                    <span className="reject-field-hint">Pre-filled from your account. Edit if needed.</span>
                                </div>
                                <div className="reject-form-group">
                                    <label>Subject</label>
                                    <input
                                        type="text"
                                        value={rejectSubject}
                                        onChange={(e) => setRejectSubject(e.target.value)}
                                        className="reject-input"
                                    />
                                </div>
                                <div className="reject-form-group">
                                    <label>Custom message (optional)</label>
                                    <textarea
                                        value={rejectCustomMessage}
                                        onChange={(e) => setRejectCustomMessage(e.target.value)}
                                        className="reject-textarea"
                                        placeholder="Add a personalised note to include in the email..."
                                        rows={3}
                                    />
                                </div>

                                <div className="reject-modal-actions">
                                    <button className="btn-cancel" onClick={closeRejectModal}>Cancel</button>
                                    <button
                                        className="btn-confirm-reject"
                                        onClick={handleSendRejection}
                                        disabled={rejectSending}
                                    >
                                        {rejectSending ? 'Sending...' : 'Confirm & Send'}
                                    </button>
                                </div>
                            </div>
                        ) : rejectResult.error ? (
                            <div className="reject-modal-body">
                                <div className="reject-result-error">
                                    <p>Failed to send rejection emails:</p>
                                    <p className="reject-error-msg">{rejectResult.error}</p>
                                </div>
                                <div className="reject-modal-actions">
                                    <button className="btn-cancel" onClick={closeRejectModal}>Close</button>
                                    <button className="btn-confirm-reject" onClick={handleSendRejection}>Retry</button>
                                </div>
                            </div>
                        ) : (
                            <div className="reject-modal-body">
                                <div className="reject-result-summary">
                                    <h4>Results</h4>
                                    <div className="reject-result-stats">
                                        {rejectResult.sent > 0 && <span className="stat-sent">{rejectResult.sent} sent</span>}
                                        {rejectResult.failed > 0 && <span className="stat-failed">{rejectResult.failed} failed</span>}
                                        {rejectResult.no_email > 0 && <span className="stat-noemail">{rejectResult.no_email} no email</span>}
                                    </div>
                                </div>
                                {rejectResult.details && (
                                    <table className="reject-preview-table reject-result-table">
                                        <thead>
                                            <tr>
                                                <th>Candidate</th>
                                                <th>Email</th>
                                                <th>Status</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {rejectResult.details.map((d, i) => (
                                                <tr key={i} className={`status-${d.status}`}>
                                                    <td>{d.candidate_name}</td>
                                                    <td>{d.email || '-'}</td>
                                                    <td>
                                                        <span className={`status-badge status-${d.status}`}>
                                                            {d.status === 'sent' ? 'Sent' : d.status === 'failed' ? 'Failed' : 'No Email'}
                                                        </span>
                                                        {d.error && <span className="status-error-detail"> - {d.error}</span>}
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                )}
                                <div className="reject-modal-actions">
                                    <button className="btn-confirm-reject" onClick={closeRejectModal}>Done</button>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            )}

            {showScreeningInviteModal && (
                <div
                    className="reject-modal-overlay"
                    onClick={(e) => {
                        if (e.target === e.currentTarget) closeScreeningInviteModal();
                    }}
                >
                    <div className="reject-modal">
                        <div className="reject-modal-header">
                            <h2>Send Recruiter Screening Invites</h2>
                            <button className="reject-modal-close" onClick={closeScreeningInviteModal} type="button">
                                &times;
                            </button>
                        </div>

                        {!screeningResult ? (
                            <div className="reject-modal-body">
                                <div className="reject-candidate-preview">
                                    <table className="reject-preview-table">
                                        <thead>
                                            <tr>
                                                <th>Candidate</th>
                                                <th>Email</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {filteredAndSortedCandidates
                                                .filter((c) => selectedCandidates.has(c.id))
                                                .map((c) => (
                                                    <tr key={c.id} className={!c.email ? 'no-email-row' : ''}>
                                                        <td>{c.candidate_name || c.filename || 'Unknown'}</td>
                                                        <td>
                                                            {c.email || (
                                                                <span className="no-email-label">No email on file</span>
                                                            )}
                                                        </td>
                                                    </tr>
                                                ))}
                                        </tbody>
                                    </table>
                                </div>
                                <div className="reject-form-group">
                                    <label>Recruiter email (Teams calendar)</label>
                                    <input
                                        type="email"
                                        value={screeningRecruiterEmail}
                                        onChange={(e) => setScreeningRecruiterEmail(e.target.value)}
                                        className="reject-input"
                                        placeholder="recruiter@company.com"
                                    />
                                </div>
                                <div className="reject-form-group">
                                    <label>Duration (minutes)</label>
                                    <input
                                        type="number"
                                        min={15}
                                        max={120}
                                        value={screeningDuration}
                                        onChange={(e) => setScreeningDuration(e.target.value)}
                                        className="reject-input"
                                    />
                                </div>
                                <div className="reject-modal-actions">
                                    <button className="btn-cancel" onClick={closeScreeningInviteModal} type="button">
                                        Cancel
                                    </button>
                                    <button
                                        className="btn-confirm-reject"
                                        onClick={handleSendScreeningInvites}
                                        disabled={screeningSending}
                                        type="button"
                                    >
                                        {screeningSending ? 'Sending…' : 'Send slot options'}
                                    </button>
                                </div>
                            </div>
                        ) : screeningResult.error ? (
                            <div className="reject-modal-body">
                                <div className="reject-result-error">
                                    <p>Failed to send screening invites:</p>
                                    <p className="reject-error-msg">{screeningResult.error}</p>
                                </div>
                                <div className="reject-modal-actions">
                                    <button className="btn-cancel" onClick={closeScreeningInviteModal} type="button">
                                        Close
                                    </button>
                                    <button
                                        className="btn-confirm-reject"
                                        onClick={handleSendScreeningInvites}
                                        type="button"
                                    >
                                        Retry
                                    </button>
                                </div>
                            </div>
                        ) : (
                            <div className="reject-modal-body">
                                <div className="reject-result-summary">
                                    <h4>Results</h4>
                                    <div className="reject-result-stats">
                                        {screeningResult.sent > 0 && (
                                            <span className="stat-sent">{screeningResult.sent} sent</span>
                                        )}
                                        {screeningResult.failed > 0 && (
                                            <span className="stat-failed">{screeningResult.failed} failed</span>
                                        )}
                                        {screeningResult.skipped > 0 && (
                                            <span className="stat-noemail">{screeningResult.skipped} skipped</span>
                                        )}
                                    </div>
                                </div>
                                {screeningResult.details && (
                                    <table className="reject-preview-table reject-result-table">
                                        <thead>
                                            <tr>
                                                <th>Resume ID</th>
                                                <th>Status</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {screeningResult.details.map((d, i) => (
                                                <tr key={i} className={`status-${d.status}`}>
                                                    <td>{d.resume_id}</td>
                                                    <td>
                                                        <span className={`status-badge status-${d.status}`}>
                                                            {d.status}
                                                        </span>
                                                        {d.reason && (
                                                            <span className="status-error-detail"> — {d.reason}</span>
                                                        )}
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                )}
                                <div className="reject-modal-actions">
                                    <button
                                        className="btn-confirm-reject"
                                        onClick={closeScreeningInviteModal}
                                        type="button"
                                    >
                                        Done
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default CandidateListPage;
